import Vue from 'vue';
import {Action, Module, Mutation, MutationAction, VuexModule} from 'vuex-module-decorators';
import type {LoginResponse, UserLoginForm, UserProfile} from '@pazion/pazion-core/src/models/user-models';
import {fetchUserData, getUserConfig, loginUser, logoutUser} from '@pazion/pazion-core/src/services/auth-service';
import {permission} from "@pazion/pazion-core/src/store/index";
import {allowedDefaultRedirect} from "@pazion/pazion-core/src/services/base-service";
import router from "@pazion/pazion-core/src/router";

export interface IAuthenticationState {
    user: LoginResponse | null;
    authenticated: boolean;
    backupUser: LoginResponse | null;
    getProfile: UserProfile | null;
    baseUrl: string;
    apiUrl: string;
    userConfig: any
}


@Module({name: 'authentication', namespaced: true})
export default class AuthenticationModule extends VuexModule implements IAuthenticationState {

    public authenticated = false;
    public baseUrl = `${process.env.VUE_APP_BACKEND_BASE_URL}`;
    public apiUrl = `${process.env.VUE_APP_BACKEND_API_ROOT}`;
    public user: LoginResponse | null = null;
    public backupUser: LoginResponse | null = null;
    private profile: UserProfile | null = null;
    public userConfig: any = null;

    get getBaseUrl() {
        return this.baseUrl;
    }

    get getApiUrl(): string {
        return this.apiUrl;
    }

    @Mutation
    public loginSuccess(user: LoginResponse): void {
        this.authenticated = true;
        this.user = user;
    }

    @Mutation
    public logout(): void {
        if (this.backupUser) {
            this.user = this.backupUser;
            this.backupUser = null;
            return;
        } else {
            this.authenticated = false;
            this.user = null;
        }
    }

    @Mutation
    public setUserConfig(config: any): void {
        this.userConfig = config
    }

    @Action({rawError: true})
    public afterLogout() {
        if (router.currentRoute.meta?.guest === true || router.currentRoute.name === 'login') {
            return;
        }
        if (allowedDefaultRedirect(Vue)) {
            router.push({name: 'login'});
        }
    }

    get isAuthenticated(): boolean {
        return this.authenticated;
    }

    get isAdmin(): boolean {
        return this.profile?.roles.includes('administrator') ?? false;
    }

    get isSystem(): boolean {
        return this.profile?.isSystem || false;
    }

    get isTenant(): boolean {
        return typeof (this.profile?.tenantId ?? false) == "number";
    }

    // 
    @Action
    public async verifyUser() {
        if (!allowedDefaultRedirect(Vue)) {
            //@ts-ignore Vue 
            router.push({name: 'membership pass', query: {...Vue.$route.query}})
        }
        const ud = fetchUserData(this.user?.token);
        const userConfig = getUserConfig(this.user);

        this.setUserConfig(userConfig);
        if (ud === null || this.user === null || this.user.refreshToken === null) {
            this.context.commit("logout");
            this.afterLogout();
            return;
        }

        await permission.updatePermissons(userConfig);
        await this.context.commit("updateUserProfile", ud);
        await this.updateRoutes();
    }

    @Mutation
    public updateUserProfile(profile: UserProfile) {
        this.profile = profile;
        if (this.profile.roles.includes("administrator")) {
            this.profile.roles.push("user");
        }
    }

    @Mutation
    public updateBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    @Mutation
    public updateApiUrl(apiUrl: string) {
        this.apiUrl = apiUrl;
    }

    @Action({commit: 'updateUserProfile'})
    public async loadProfile() {
        await this.verifyUser();
        if (!this.isUserLoggedIn || this.user === null) {
            return;
        }
        return fetchUserData(this.user.token);
    }

    @Action
    public async updateRoutes() {
        if (!this.profile) {
            return;
        }
        await permission.updateRoutes();
    }

    @MutationAction({mutate: ['user', 'backupUser']})
    public async loginAsUser(data: LoginResponse) {
        let backupUser = this.user;
        let user = data;
        return {user, backupUser};
    }

    @MutationAction({mutate: ['user', 'authenticated']})
    public async login(data: UserLoginForm) {
        const user = await loginUser(data);
        let authenticated = false;
        if (user.token !== undefined) {
            authenticated = !!user.token;
        }
        return {user, authenticated};
    }

    @Action
    public signOut(): void {
        logoutUser();
        this.context.commit('logout');
    }

    get isUserLoggedIn() {
        return this.authenticated;
    }

    get profileLoaded() {
        return this.getProfile !== null;
    }

    get getProfile(): UserProfile | null {
        return this.profile;
    }
}
